/**
 * Formats a time difference into a human-readable string
 * e.g., "5 min ago", "2 hr ago", "1 day ago"
 */
export function formatTimeAgo(timestamp: string): string {
  const now = Date.now();
  const time = new Date(timestamp).getTime();
  const diffMs = now - time;

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (minutes < 1) {
    return "Just now";
  } else if (minutes < 60) {
    return `${minutes} min ago`;
  } else if (hours < 24) {
    return `${hours} hr ago`;
  } else {
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  }
}

/**
 * Calculates overdue time from a given timestamp
 * Subtracts the threshold (default 360 minutes) and formats as "X hr Y min"
 * Returns empty string if not overdue or timestamp is invalid
 */
export function formatOverdueTime(
  timestamp: string | number | undefined | null,
  thresholdMinutes: number = 360
): string {
  if (!timestamp) return "";

  const now = Date.now();
  const time = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();
  const totalMinutes = Math.floor((now - time) / (1000 * 60));
  const overdueMinutes = Math.max(0, totalMinutes - thresholdMinutes);

  if (overdueMinutes === 0) return "";

  if (overdueMinutes < 60) {
    return `${overdueMinutes} min`;
  } else {
    const hours = Math.floor(overdueMinutes / 60);
    const mins = overdueMinutes % 60;
    return `${hours} hr ${mins} min`;
  }
}

/**
 * Formats elapsed time from a timestamp as "X hr Y min ago"
 * Similar to formatTimeAgo but always shows hours and minutes
 */
export function formatElapsedTime(timestamp: string | number | undefined | null): string {
  if (!timestamp) return "";

  const now = Date.now();
  const time = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();
  const diffMinutes = Math.floor((now - time) / (1000 * 60));

  if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  } else {
    const hours = Math.floor(diffMinutes / 60);
    const mins = diffMinutes % 60;
    return `${hours} hr ${mins} min ago`;
  }
}

